@use "@styles/tokens";

// Visual source of truth: docs/specs/2026-09-28-avatar-figma-rework.md,
// docs/specs/2026-09-29-entity-class-workspace-avatars.md and
// docs/specs/2026-10-23-colorless-people-avatars.md (tone).
// Sizes and the diamond are Figma geometry with no token
// family, so they live in component-scoped custom properties.

@layer base-components {
    .avatar {
        // Override hatch (spec "Override hatch"): set these via className.
        // --avatar-bg set there wins over tone and variant. The `color` prop
        // arrives inline as --avatar-color.
        --avatar-size: 20px;
        --avatar-color: var(--color-ocean-blue-200);
        --avatar-bg: var(--avatar-color);
        --avatar-fg: var(--fg-primary);
        --avatar-radius: 50%;

        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: var(--avatar-size);
        height: var(--avatar-size);
        overflow: hidden;
        border-radius: var(--avatar-radius);
        color: var(--avatar-fg);
        background-color: var(--avatar-bg);
        user-select: none;

        // Figma's EntityIcon corner at every size; firm logos share it (spec B1).
        &[data-shape="square"] {
            --avatar-radius: var(--radius-xs);
        }

        &[data-shape="diamond"] {
            // ds-deviation: Figma ClassIcon geometry, no token family. Figma
            // draws a --radius-xs square rotated 45deg whose rounded tips touch
            // the box (13.647px at 16px). 0.8284 is 2 * (sqrt(2) - 1), 0.7071
            // is 1 / sqrt(2).
            --avatar-diamond-outer: calc(
                (var(--avatar-size) + var(--radius-xs) * 0.8284) * 0.7071
            );
            --avatar-radius: var(--radius-xs);

            isolation: isolate;
            overflow: visible;
            background-color: transparent;

            &::before {
                content: "";
                position: absolute;
                inset: 0;
                z-index: -1;
                width: var(--avatar-diamond-outer);
                height: var(--avatar-diamond-outer);
                margin: auto;
                border-radius: var(--avatar-radius);
                background-color: var(--avatar-bg);
                transform: rotate(45deg);
            }
        }

        // Opaque, unlike --bg-neutral, so overlapping avatars in a stack stay clean.
        &[data-tone="neutral"] {
            --avatar-bg: var(--bg-tertiary);
        }

        &[data-variant="outline"] {
            --avatar-bg: var(--bg-primary);
            --avatar-ring: var(--border-default);

            box-shadow: inset 0 0 0 var(--border-width) var(--avatar-ring);
        }

        &[data-variant="outline"][data-tone="colorful"] {
            --avatar-ring: var(--avatar-color);
        }

        &[data-variant="outline"][data-shape="diamond"] {
            box-shadow: none;

            &::before {
                box-shadow: inset 0 0 0 var(--border-width) var(--avatar-ring);
            }
        }

        &[data-size="xs"] {
            @include tokens.text-3xs-8-medium;

            --avatar-size: 16px;
        }

        &[data-size="sm"] {
            @include tokens.text-2xs-10-medium;

            --avatar-size: 20px;
        }

        &[data-size="md"] {
            @include tokens.text-2xs-10-medium;

            --avatar-size: 24px;
        }

        &[data-size="lg"] {
            @include tokens.text-xs-12-medium;

            --avatar-size: 32px;
        }

        // Entity and class icons keep 10px letters at 16px (spec B5).
        &[data-size="xs"]:not([data-shape="circle"]) {
            @include tokens.text-2xs-10-medium;
        }
    }

    .image {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .fallback {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;

        > svg {
            width: 100%;
            height: 100%;
        }
    }
}
